import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import _ from "underscore";
import { useIntersection } from "Hooks/customHooks";
import { START_GET_PATIENT_FIELD_LIST } from "Actions/GroupAction";
import {
  Checkbox,
  Box,
  Chip,
  CircularProgress,
  Grid,
  TextField,
  Card,
  Button
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete from "@mui/material/Autocomplete";
import { getLocalStorage } from "utils/generalUtils";
import HtmlTooltipMain from "Components/Common/HtmlTooltipMain";

const useStyles = makeStyles((theme) => ({
  option: {
    paddingBottom: "0px !important",
  },
  groupColorIcon: {
    padding: "8px",
    margin: "3px",
    borderRadius: "3px !important",
  },
  PatientAutocompleteListbox: {
    padding: "0px !important",
    // marginTop: "45px !important",
  },
}));

function PatientAutoComplete(props) {
  const {
    value,
    multiple,
    disabled,
    handleChange,
    patientFieldList,
    getPatientFieldAction,
    apiParams,
    disableEnter = true,
    maxVisibleChip = 2,
    error = false,
    helperText,
    groupRecordActionType,
    buttonOptionTop,
    buttonTitle,
    handleButtonClick,
    textFieldProps = {
      required: true,
    },
    ...other
  } = props;
  const classes = useStyles();
  const [selectPatient, setSelectPatient] = useState(value || {});
  const [isOpen, setIsOpen] = useState(false);
  const [patientSearchString, setDrugPatientSearchString] = useState("");
  const [setTargetPatient, entryPatient] = useIntersection({ threshold: 0.1 });
  const patientRecordLimit = 20;

  /* Value selection */
  useEffect(() => {
    if (multiple) {
      setSelectPatient(Object.values(value || {}));
    } else {
      setSelectPatient(value);
    }
  }, [value]);

  /* Search Debounce Function */
  const searchPatientDebounce = useRef(
    _.debounce((searchString, open) => {
      let params = {
        offset: 0,
      };
      if (searchString) {
        params.q = searchString;
      }
      if (open) {
        getPatientFieldAction(params);
      }
    }, 500)
  );

  /* Handle Search Function */
  const handleSearchPatientName = (newValue) => {
    setDrugPatientSearchString(newValue);
    searchPatientDebounce.current(newValue, isOpen);
  };

  /* Show data based on scroll pagination */
  useEffect(() => {
    if (entryPatient.isIntersecting && patientFieldList.loading === false) {
      let params = {
        offset: patientFieldList?.data?.list?.length || 0,
        ...(patientSearchString && { q: patientSearchString }),
      };
      if (isOpen) {
        getPatientFieldAction(params);
      }
    }
  }, [entryPatient]);

  /* Fetch List */
  let fetchPatient = () => {
    setIsOpen(true);
    let params = {
      offset: 0,
    };
    if (patientSearchString) {
      params.q = patientSearchString;
    }
    getPatientFieldAction(params);
  };

  /* Patient List loading useEffect */
  useEffect(() => {
    if (
      !patientFieldList?.loading &&
      !Object.keys(patientFieldList?.data)?.length
    ) {
      getPatientFieldAction({ offset: 0, limit: patientRecordLimit });
    }
  }, [patientFieldList]);

  /* Handle Change Patient List */
  const handleChangePatientList = (event, newValue) => {
    let newList = {};
    if (multiple) {
      newValue.map((patientList) => {
        newList[patientList.id] = patientList;
      });
    } else {
      newList = newValue;
    }
    setSelectPatient(newValue);
    handleChange && handleChange(newList);
  };

  /** Filter options with a custom button at the top of the list */
  const filterOptions = (options) => {
    let result = [...options];
    if (buttonOptionTop && !result.some((option) => option.type === "button")) {
      result = [{ type: "button" }, ...result];
    }
    if (
      patientFieldList?.data?.list?.length < patientFieldList?.data?.total
    ) {
      result.push("FinalOption");
    }
    return result || [];
  };

  return (
    <>
      <Autocomplete
        multiple={multiple}
        classes={{
          listbox: classes.PatientAutocompleteListbox,
          option: classes.option,
        }}
        disableCloseOnSelect={multiple}
        disabled={Boolean(disabled)}
        onOpen={fetchPatient}
        onClose={() => {
          setIsOpen(false);
        }}
        loading={patientFieldList?.loading}
        noOptionsText={"No results found"}
        value={selectPatient}
        onChange={handleChangePatientList}
        options={patientFieldList?.data?.list || []}
        getOptionLabel={(option) => option?.name || ""}
        onInputChange={(event, newValue) => {
          handleSearchPatientName(newValue);
        }}
        isOptionEqualToValue={(option, value) => {
          return option.id === value.id;
        }}
        filterOptions={filterOptions}
        getOptionDisabled={(option) => {
          if (option?.group_detail?.g_name) {
            return true;
          }
        }}
        renderOption={(props, option, { selected }) => {
          if (buttonOptionTop && option.type === "button") {
            return (
              <Grid
                container
                alignItems="center"
                className="autoCompleteButton"
              >
                <Button
                  onClick={handleButtonClick}
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  {buttonTitle}
                </Button>
              </Grid>
            );
          } else {
          return (
            <React.Fragment>
              {typeof option === "object" ? (
                <Grid {...props} key={option?.id}>
                  {multiple && (
                    <Checkbox
                      checked={
                        option?.group_detail?.g_name ===
                        getLocalStorage("group_name")
                          ? "a"
                          : selected
                      }
                    />
                  )}
                  {option.name}
                  {` `} {option?.last_name}
                  {option?.group_detail?.g_name && (
                    <>
                      {`  (`}
                      <Card
                        className={classes.groupColorIcon}
                        style={{
                          background: option?.group_detail?.g_color_code,
                        }}
                      />
                      {option?.group_detail?.g_name || ""}
                      {`)`}
                    </>
                  )}
                </Grid>
              ) : (
                <Grid
                  className="autoCompleteInputLoader"
                  ref={setTargetPatient}
                >
                  <CircularProgress size={20} />
                </Grid>
              )}
            </React.Fragment>
          );
          }
        }}
        renderTags={(tagValue, getTagProps) => {
          if (tagValue.length === maxVisibleChip) {
            return tagValue.map((option, index) => {
              return (
                <Chip
                  key={index}
                  label={`${option.name}`}
                  {...getTagProps({ index })}
                  size="small"
                />
              );
            });
          } else {
            return (
              <Box display="flex" alignItems="center">
                {tagValue.slice(0, maxVisibleChip).map((option, index) => (
                  <Chip
                    label={`${tagValue[index].name}`}
                    {...getTagProps({ index: index })}
                    size="small"
                  />
                ))}
                {tagValue.length > maxVisibleChip && (
                  <HtmlTooltipMain
                    interactive={true}
                    title={
                      <Grid>
                        {tagValue.slice(maxVisibleChip).map((option, index) => (
                          <Box>{option.name}</Box>
                        ))}
                      </Grid>
                    }
                  >
                    <Box>
                      +{" "}
                      {tagValue.length -
                        Math.min(maxVisibleChip, tagValue.length)}{" "}
                      more
                    </Box>
                  </HtmlTooltipMain>
                )}
              </Box>
            );
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              groupRecordActionType === "addPatientInGroup"
                ? "Add Patient *"
                : "Add Patient"
            }
            placeholder={
              groupRecordActionType === "addPatientInGroup"
                ? "Add Patient *"
                : "Add Patient"
            }
            error={error}
            helperText={helperText}
            InputLabelProps={{ required: false }}
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password",
            }}
          />
        )}
        {...other}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  patientFieldList: state.group.patientFieldList,
});

const mapDispatchToProps = (dispatch) => ({
  getPatientFieldAction: (payload) =>
    dispatch({ type: START_GET_PATIENT_FIELD_LIST, payload }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PatientAutoComplete);
